import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { overtimeService } from "../../api/overtimeService";
import Badge from "../../components/ui/Badge";
import ApprovalProgress from "../../components/ui/ApprovalProgress";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import Tabs from "../../components/ui/Tabs";
import { useToast } from "../../components/ui/useToast";
import { getErrorMessage } from "../../utils/apiError";
import { formatDate } from "../../utils/date";
import { formatManilaDateTime, formatManilaTime } from "../../utils/manilaTime";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import {
  formatMinutes,
  OVERTIME_KEY,
  OVERTIME_RULE_TEXT,
  OVERTIME_STATUS,
  STATUS_TABS,
} from "./overtimeUtils";
import FileOvertimeModal from "./FileOvertimeModal";
import ui from "../../components/ui/ui.module.css";

const COLUMN_COUNT = 7;

// /my-overtime?file=1&date=YYYY-MM-DD opens the form (dashboard / DTR links).
const MyOvertimeView = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const hasEmployee = !!getEmployeeId();
  const [searchParams, setSearchParams] = useSearchParams();

  const [status, setStatus] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [cancelTarget, setCancelTarget] = useState(null);
  const isFormOpen = searchParams.get("file") === "1";

  const filters = { status, from, to };
  const query = useQuery({
    queryKey: [OVERTIME_KEY, "my", filters],
    queryFn: () => overtimeService.getMine(filters),
    enabled: hasEmployee,
  });
  const rows = Array.isArray(query.data) ? query.data : [];

  const cancelMutation = useMutation({
    mutationFn: (id) => overtimeService.cancel(id),
    onSuccess: () => toast.success("Overtime request cancelled."),
    onError: (error) =>
      toast.error(getErrorMessage(error, "Unable to cancel the request.")),
    onSettled: () => {
      setCancelTarget(null);
      queryClient.invalidateQueries({ queryKey: [OVERTIME_KEY] });
    },
  });

  const openForm = () => setSearchParams({ file: "1" });
  const closeForm = () => setSearchParams({});

  if (!hasEmployee) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        Your account is not linked to an employee record, so overtime is not
        available.
      </div>
    );
  }

  const message = query.isLoading
    ? "Loading your overtime requests…"
    : query.isError
      ? getErrorMessage(query.error, "Unable to load your overtime requests.")
      : rows.length === 0
        ? "No overtime requests."
        : "";

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">My Overtime</h4>
          <p className="page-subtitle">{OVERTIME_RULE_TEXT}</p>
        </div>
        <button
          type="button"
          className={cx(ui.button, ui.primary)}
          onClick={openForm}
        >
          <i className="bi bi-plus-lg"></i>
          File overtime
        </button>
      </div>

      <div className={ui.card}>
        <Tabs tabs={STATUS_TABS} active={status} onChange={setStatus} />

        <div className={ui.filters}>
          <label className={ui.hint} htmlFor="ot-from">
            From
          </label>
          <input
            id="ot-from"
            type="date"
            className={ui.input}
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
          <label className={ui.hint} htmlFor="ot-to">
            To
          </label>
          <input
            id="ot-to"
            type="date"
            className={ui.input}
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
          />
          {(from || to) && (
            <button
              type="button"
              className={ui.linkButton}
              onClick={() => {
                setFrom("");
                setTo("");
              }}
            >
              Clear dates
            </button>
          )}
        </div>

        <div className={ui.tableWrapper}>
          <table className={ui.table}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Requested</th>
                <th>Counted</th>
                <th>Status</th>
                <th>Reason / remarks</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {message && (
                <tr>
                  <td
                    colSpan={COLUMN_COUNT}
                    className={
                      query.isError
                        ? "text-danger"
                        : cx(ui.muted, "text-center")
                    }
                  >
                    {message}
                  </td>
                </tr>
              )}

              {rows.map((request) => {
                const config = OVERTIME_STATUS[request.status] ?? {
                  label: request.status,
                  tone: "gray",
                };
                const isApproved = request.status === "APPROVED";
                const trimmed =
                  isApproved &&
                  request.approvedMinutes != null &&
                  request.approvedMinutes < request.requestedMinutes;

                return (
                  <tr key={request.id}>
                    <td className={cx(ui.noWrap, ui.strong)}>
                      {formatDate(request.date)}
                    </td>
                    <td className={ui.noWrap}>
                      {formatManilaTime(request.startTime)} –{" "}
                      {formatManilaTime(request.endTime)}
                    </td>
                    <td className={ui.noWrap}>
                      {formatMinutes(request.requestedMinutes)}
                    </td>
                    <td className={ui.noWrap}>
                      {isApproved ? (
                        <>
                          {formatMinutes(request.countedMinutes)}
                          {trimmed && (
                            <div className={ui.muted}>
                              approved {formatMinutes(request.approvedMinutes)}
                            </div>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td>
                      <Badge tone={config.tone}>{config.label}</Badge>
                      {request.reviewedAt && request.status !== "PENDING" && (
                        <div className={ui.muted}>
                          {formatManilaDateTime(request.reviewedAt)}
                        </div>
                      )}
                      <ApprovalProgress
                        isPending={request.status === "PENDING"}
                        current={request.levels?.current ?? 1}
                        total={request.levels?.total ?? 1}
                        approvals={request.approvals ?? []}
                        waitingFor={request.waitingFor}
                        managersAbove={request.managersAbove}
                        formatExtra={(entry) =>
                          entry.action === "APPROVE" &&
                          entry.approvedMinutes != null
                            ? formatMinutes(entry.approvedMinutes)
                            : null
                        }
                      />
                    </td>
                    <td>
                      <div className={ui.truncate} title={request.reason}>
                        {request.reason}
                      </div>
                      {request.reviewRemarks && (
                        <div
                          className={
                            request.status === "REJECTED"
                              ? "text-danger"
                              : ui.muted
                          }
                        >
                          {request.status === "REJECTED"
                            ? "Reason: "
                            : "Approver: "}
                          {request.reviewRemarks}
                        </div>
                      )}
                    </td>
                    <td className="text-end">
                      {request.canCancel && (
                        <button
                          type="button"
                          className={cx(
                            ui.button,
                            ui.dangerOutline,
                            ui.buttonSmall,
                          )}
                          onClick={() => setCancelTarget(request)}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {isFormOpen && (
        <FileOvertimeModal
          initialDate={searchParams.get("date")}
          onClose={closeForm}
        />
      )}

      {cancelTarget && (
        <ConfirmDialog
          title="Cancel overtime request?"
          message={`Cancel your overtime request for ${formatDate(cancelTarget.date)} (${formatManilaTime(cancelTarget.startTime)} – ${formatManilaTime(cancelTarget.endTime)})?`}
          confirmLabel="Cancel request"
          tone="danger"
          isPending={cancelMutation.isPending}
          onConfirm={() => cancelMutation.mutate(cancelTarget.id)}
          onClose={() => setCancelTarget(null)}
        />
      )}
    </div>
  );
};

export default MyOvertimeView;
